"use client"

import type React from "react"

import { useEffect, useState, useRef, type FormEvent } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Form, Spinner, Alert } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { ArrowLeft, Send, Paperclip, XCircle, FileText, ImageIcon, File } from "lucide-react"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { getGuestById, addGuestComment, clearCurrentGuest } from "../../redux/slices/guestsSlice"
import { formatDate } from "../../utils/timestamp"
import { useAuth } from "../../contexts/auth-context"
import AttachmentPreviewModal from "../../components/AttachmentPreviewModal"

export default function GuestThreadPage() {
  const { guestId } = useParams<{ guestId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { currentGuest, isLoading, error } = useAppSelector((state) => state.guests)
  const { user } = useAuth()
  const [newComment, setNewComment] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [attachment, setAttachment] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [showAttachmentModal, setShowAttachmentModal] = useState(false)
  const [selectedAttachment, setSelectedAttachment] = useState<{
    url: string
    name: string
    type: string
  } | null>(null)

  const isAdmin = user?.role === "admin"

  useEffect(() => {
    if (guestId) {
      dispatch(getGuestById(guestId))
    }

    return () => {
      dispatch(clearCurrentGuest())
    }
  }, [dispatch, guestId])

  useEffect(() => {
    scrollToBottom()
  }, [currentGuest?.comments])

  const scrollToBottom = () => {
    setTimeout(() => {
      const isMobile = window.innerWidth < 768

      if (isMobile) {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        })
      } else {
        const desktopContainer = document.querySelector(".d-none.d-md-flex .overflow-auto")
        if (desktopContainer) {
          desktopContainer.scrollTop = desktopContainer.scrollHeight
          return
        }
      }

      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        })
      }
    }, 200)
  }

  const handleSubmitComment = async (e: FormEvent) => {
    e.preventDefault()
    if ((!newComment.trim() && !attachment) || !guestId) return

    setSubmitting(true)
    try {
      await dispatch(addGuestComment({ guestId, content: newComment, attachment })).unwrap()
      setNewComment("")
      setAttachment(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    } catch (error) {
      console.error("Failed to add comment:", error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0])
    }
  }

  const clearAttachment = () => {
    setAttachment(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <ImageIcon className="size-4" />
    } else if (fileType.includes("pdf")) {
      return <FileText className="size-4" />
    } else {
      return <File className="size-4" />
    }
  }

  const handleAttachmentClick = (url: string, name: string, type: string) => {
    setSelectedAttachment({ url, name, type })
    setShowAttachmentModal(true)
  }

  const backPath = isAdmin ? "/dashboard/admin/guests" : "/dashboard/guests"

  if (isLoading && !currentGuest) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-3">
        {error}
      </Alert>
    )
  }

  if (!currentGuest) {
    return (
      <Alert variant="warning" className="my-3">
        {t("guestNotFound")}
      </Alert>
    )
  }

  const headerTitle = `${currentGuest.fullName} — ${currentGuest.userFullName} (${currentGuest.roomNumber})`

  const renderComment = (comment: any, isMobile: boolean) => {
    const isCurrentUser = comment.userId === user?.id
    const avatarSize = isMobile ? "40px" : "45px"
    const maxWidth = isMobile ? "85%" : "75%"

    return (
      <div key={comment.id} className={`d-flex ${isCurrentUser ? "justify-content-end" : ""}`}>
        <div className={`d-flex gap-${isMobile ? "2" : "3"}`} style={{ maxWidth }}>
          {!isCurrentUser && (
            <div
              className="rounded-circle d-flex align-items-center justify-content-center text-white overflow-hidden flex-shrink-0"
              style={{
                width: avatarSize,
                height: avatarSize,
                minWidth: avatarSize,
                minHeight: avatarSize,
                background: "rgba(255, 255, 255, 0.2)",
              }}
            >
              {comment.userPhotoURL ? (
                <img
                  src={comment.userPhotoURL || "/placeholder.svg"}
                  alt={comment.userFullName}
                  className="w-100 h-100 rounded-circle"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                comment.userFullName.charAt(0).toUpperCase()
              )}
            </div>
          )}
          <div
            className={`rounded-3 p-3 ${isCurrentUser ? "text-primary" : "text-white"}`}
            style={{
              background: isCurrentUser ? "white" : "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div className={`d-flex justify-content-between gap-${isMobile ? "2" : "3"} mb-${isMobile ? "1" : "2"}`}>
              <span className={`${isMobile ? "small " : ""}fw-medium`}>
                {comment.userFullName}
                {comment.userRole === "admin" && <span className={`badge bg-danger ms-${isMobile ? "1" : "2"} small`}>Admin</span>}
              </span>
              <span className="small opacity-75">{formatDate(comment.createdAt)}</span>
            </div>
            {comment.content && (
              <p className={`mb-0${isMobile ? " small" : ""}`} style={{ whiteSpace: "pre-wrap" }}>
                {comment.content}
              </p>
            )}

            {comment.hasAttachment && (
              <div className={`mt-${isMobile ? "2" : "3"}`}>
                <div
                  onClick={() =>
                    handleAttachmentClick(
                      comment.attachmentUrl || "",
                      comment.attachmentName || "Attachment",
                      comment.attachmentType || "",
                    )
                  }
                  className={`d-flex align-items-center gap-2 p-${isMobile ? "2" : "3"} rounded cursor-pointer ${isCurrentUser ? "bg-white text-primary" : "bg-primary-subtle"}`}
                  style={{ cursor: "pointer" }}
                >
                  {getFileIcon(comment.attachmentType || "")}
                  <span className={`${isMobile ? "small " : ""}text-truncate`}>{comment.attachmentName || "Attachment"}</span>
                </div>
              </div>
            )}
          </div>
          {isCurrentUser && (
            <div
              className="rounded-circle d-flex align-items-center justify-content-center text-white overflow-hidden flex-shrink-0"
              style={{
                width: avatarSize,
                height: avatarSize,
                minWidth: avatarSize,
                minHeight: avatarSize,
                background: "rgba(255, 255, 255, 0.2)",
              }}
            >
              {comment.userPhotoURL ? (
                <img
                  src={comment.userPhotoURL || "/placeholder.svg"}
                  alt={comment.userFullName}
                  className="w-100 h-100 rounded-circle"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                comment.userFullName.charAt(0).toUpperCase()
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderEmptyState = () => (
    <div className="text-center text-white py-5">
      <p className="mb-0 opacity-75">{t("noCommentsYet")}</p>
      <small className="opacity-50">{t("startConversation")}</small>
    </div>
  )

  const renderCommentForm = (isMobile: boolean) => (
    <Form onSubmit={handleSubmitComment} className={isMobile ? "px-3" : ""}>
      <Form.Group className={`mb-${isMobile ? "2" : "3"}`}>
        {!isMobile && <Form.Label className="text-white">{t("addComment")}</Form.Label>}
        <Form.Control
          as="textarea"
          rows={isMobile ? 4 : 6}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={t("typeYourMessage")}
          disabled={submitting}
          className="bg-white border-0 rounded-3"
          size={isMobile ? "lg" : undefined}
        />
      </Form.Group>

      {attachment && (
        <div
          className={`mb-3 p-${isMobile ? "2" : "3"} rounded-3 d-flex justify-content-between align-items-center`}
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <div className="d-flex align-items-center gap-2 text-white">
            {getFileIcon(attachment.type)}
            <span className={`${isMobile ? "small " : ""}text-truncate`}>{attachment.name}</span>
          </div>
          <button className="p-0 text-white" onClick={clearAttachment}>
            <XCircle className="size-4" />
          </button>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center">
        <button
          className="btn-cancel-outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={submitting}
        >
          <Paperclip className="size-4 me-1" />
          {isMobile ? "Attach" : t("attachFile")}
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        />

        <button
          type="submit"
          className="btn-white-pill-bold"
          disabled={(!newComment.trim() && !attachment) || submitting}
        >
          {submitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              {t("sending")}
            </>
          ) : (
            <>
              <Send className="size-4 me-2" />
              {t("sendMessage")}
            </>
          )}
        </button>
      </div>
    </Form>
  )

  return (
    <>
      {/* Mobile Layout */}
      <div className="d-md-none" style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
        <div
          className="p-3 d-flex align-items-center gap-3"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1030,
            height: "70px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <button className="btn-back-circle" onClick={() => navigate(backPath)}>
            <ArrowLeft size={20} />
          </button>
          <div className="flex-grow-1">
            <h1 className="fs-6 fw-bold mb-0 text-white text-truncate">{currentGuest.fullName}</h1>
            <small className="text-white opacity-75">
              {currentGuest.userFullName} — {t("apartment")} {currentGuest.roomNumber}
            </small>
          </div>
        </div>

        <div
          className="flex-grow-1 overflow-auto px-3"
          style={{
            paddingTop: "20px",
            marginBottom: "230px",
          }}
        >
          <div className="d-flex flex-column gap-3 py-3">
            {currentGuest.comments && currentGuest.comments.length > 0 ? (
              currentGuest.comments.map((comment) => renderComment(comment, true))
            ) : (
              renderEmptyState()
            )}
          </div>
          <div ref={messagesEndRef} />
        </div>

        <div
          className="py-3 px-2"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1030,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          {renderCommentForm(true)}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="d-none d-md-flex flex-column py-4 px-4" style={{ height: "calc(100vh - 8rem)" }}>
        <div className="d-flex align-items-center gap-3 mb-4">
          <button className="btn-back-circle" onClick={() => navigate(backPath)}>
            <ArrowLeft size={20} />
          </button>
          <div className="flex-grow-1">
            <h1 className="fs-3 fw-bold mb-1 text-white">{headerTitle}</h1>
            <div className="d-flex gap-2">
              <span className="badge bg-primary">
                {new Date(currentGuest.checkInDate).toLocaleDateString("el-GR")} — {new Date(currentGuest.checkOutDate).toLocaleDateString("el-GR")}
              </span>
              <span className="badge bg-info">{currentGuest.totalDays} {t("days")}</span>
            </div>
          </div>
        </div>

        <div
          className="flex-grow-1 d-flex flex-column rounded-4 overflow-hidden"
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            minHeight: "400px",
          }}
        >
          <div className="p-3 border-bottom border-white border-opacity-25">
            <h2 className="fs-5 mb-0 text-white">{t("conversation")}</h2>
          </div>

          <div className="flex-grow-1 overflow-auto p-4" style={{ maxHeight: "calc(100vh - 300px)" }}>
            <div className="d-flex flex-column gap-4">
              {currentGuest.comments && currentGuest.comments.length > 0 ? (
                currentGuest.comments.map((comment) => renderComment(comment, false))
              ) : (
                renderEmptyState()
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="border-top border-white border-opacity-25 p-4">
            {renderCommentForm(false)}
          </div>
        </div>
      </div>

      {/* Attachment Preview Modal */}
      {selectedAttachment && (
        <AttachmentPreviewModal
          show={showAttachmentModal}
          onHide={() => setShowAttachmentModal(false)}
          attachmentUrl={selectedAttachment.url}
          attachmentName={selectedAttachment.name}
          attachmentType={selectedAttachment.type}
        />
      )}
    </>
  )
}
